import {
  PLANNED_MEDICATION_FIELD_LABEL,
  PLANNED_MEDICATION_SOURCE_LABEL,
  type PlannedMedicationVersion,
} from "@care-by-ella/contracts";

// TES-1185 (TES-1180 point 1) — the planned medication as it stands, and its
// history: "Den gemensamma strukturerade läkemedelsposten ska visa aktuell
// version och ändringshistorik" (A-02 2026-10-08). Read-only; the record is
// written only by the meeting's and the decision's own publishes.

const PARTS = [
  "medication",
  "strength",
  "dose",
  "frequency",
  "duration",
  "plan",
] as const;

function day(iso: string): string {
  return iso.slice(0, 10);
}

function Parts({
  v,
}: {
  v: Pick<PlannedMedicationVersion, (typeof PARTS)[number]>;
}) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-sm gap-y-[2px] text-sm">
      {PARTS.filter((k) => v[k]).map((k) => (
        <div key={k} className="contents">
          <dt className="text-textSecondary">
            {PLANNED_MEDICATION_FIELD_LABEL[k]}
          </dt>
          <dd className="whitespace-pre-wrap">{v[k]}</dd>
        </div>
      ))}
    </dl>
  );
}

export function PlannedMedicationSummary({
  current,
  history,
}: {
  current: Omit<PlannedMedicationVersion, "version"> & { version: number };
  history: readonly PlannedMedicationVersion[];
}) {
  const earlier = history.filter((v) => v.version !== current.version);
  return (
    <div
      className="rounded-md border border-brand/30 bg-brand/5 p-sm"
      data-testid="planned-medication"
    >
      <p className="mb-xs text-xs font-semibold text-textSecondary">
        {current.version > 0
          ? `Version ${current.version} · ${PLANNED_MEDICATION_SOURCE_LABEL[current.source]} ${day(current.recordedAt)}`
          : `${PLANNED_MEDICATION_SOURCE_LABEL[current.source]} ${day(current.recordedAt)}`}
      </p>
      <Parts v={current} />
      {current.changeReason ? (
        <p className="mt-xs text-xs text-textSecondary">
          Orsak till ändringen: {current.changeReason}
        </p>
      ) : null}
      {earlier.length > 0 ? (
        <details className="mt-xs text-xs">
          <summary className="cursor-pointer text-textSecondary">
            Tidigare versioner ({earlier.length})
          </summary>
          <ul className="mt-xs flex flex-col gap-sm">
            {earlier.map((v) => (
              <li key={v.version}>
                <p className="font-semibold text-textSecondary">
                  {`Version ${v.version} · ${PLANNED_MEDICATION_SOURCE_LABEL[v.source]} ${day(v.recordedAt)}`}
                </p>
                <Parts v={v} />
                {v.changeReason ? (
                  <p className="text-textSecondary">
                    Orsak till ändringen: {v.changeReason}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
